import {
  Button as SystemButton,
  ButtonProps as SystemButtonProps,
} from '@hover/blueprint';
import { captureException } from '@sentry/react';
import React, { MouseEventHandler, useEffect, useState } from 'react';
import invariant from 'tiny-invariant';

import { useCurrentPage, useMode } from 'src/shared/hooks';
import { useTrackingMetadata } from 'src/shared/hooks/useSegmentHistory';
import { ButtonPressed, buttonPressed } from 'src/shared/telemetry';

type TrackHandler = (
  properties: ButtonPressed & { button_or_link: string },
) => void;

// TODO(@hover/blueprint@1.0): remove this omit after 1.0 when this is removed,
// for now this prevents additional deprecated usage of `loading` vs `isLoading`
export interface ButtonProps extends Omit<SystemButtonProps, 'loading'> {
  pageName?: string;
  primaryCallToAction?: boolean;
  buttonLocation?: string;
  track?: TrackHandler;
}

const Button = ({
  pageName,
  primaryCallToAction = false,
  buttonLocation,
  children,
  onClick: onClickProp,
  onPress: onPressProp,
  disabled,
  label,
  track,
  type,
  ...props
}: ButtonProps) => {
  const currentPage = useCurrentPage();
  const {
    metadata: { job_id: jobId, partner_id: partnerId },
  } = useTrackingMetadata();

  const page = pageName || currentPage;
  const buttonText = typeof children === 'string' ? children : label;
  const mode = useMode();

  // Disable form submission until client-side hydration
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);

  useEffect(() => setIsSubmitDisabled(false), []);

  const onButtonPress: MouseEventHandler<HTMLButtonElement> = event => {
    // TODO(@hover/blueprint@1.0): `onPress` is slated for removal in Blueprint
    // 1.0 and for now we're handling this change here. In the future we should
    // update this component's API to use `onClick` for consistency.
    const onClick = onPressProp ?? onClickProp;

    onClick?.(event);

    try {
      invariant(page, 'Button: pageName is required when current page is null');
      invariant(
        buttonText,
        'Button: label is required when children is not string',
      );

      const properties = {
        page_or_screen_name: page,
        primary_cta: primaryCallToAction,
        button_location: buttonLocation,
        button_text: buttonText,
        design_studio_mode: mode,
        job_id: jobId,
        partner_id: partnerId,
      };

      if (track) {
        track({ button_or_link: 'button', ...properties });
      } else {
        buttonPressed(properties);
      }
    } catch (error) {
      captureException(error);
    }
  };

  return (
    <SystemButton
      {...props}
      isDisabled={disabled}
      label={label}
      onClick={onButtonPress}
      type={isSubmitDisabled && type === 'submit' ? 'button' : type}
    >
      {children}
    </SystemButton>
  );
};

export { Button };
